import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@mui/material";
import _ from "lodash";
import { useNavigate } from "react-router";
import Tooltip from "../../components/shared/Tooltip";
import { Box, VStack } from "../../components/utils";
import { globalFilter, sort } from "../../utils/arrays";
import BasicMenu from "../../components/shared/Menu";
import Pagination from "../../components/shared/Pagination";
import { limitString, _trimAll } from "../../utils/string";
import { getCurrencySymbolRoboto } from "../../utils/currencyRoboto";
import {
  THead,
  TableHeader,
  tableEntryStyle,
  tableFirstColStyle,
} from "../../components/TableComponent";
import { Action, CTADropdown } from "../../components/shared/Dropdown";
import DeleteModal from "./DeleteModal";
import { NewInstrumentState, NewSecurity } from "./RoundCreationModel";
import { EmptyTable } from "../quickRound/EmptyTableComponents";
import { useRMCreationStore } from "../../store/roundCreationStore";
import AddNewSecurity from "./AddNewSecurity";

export default function NewSecurityTable({
  instruments,
}: {
  instruments: NewSecurity[];
}) {
  let _newInstruments: NewSecurity[] = instruments;
  const roundCreationState = useRMCreationStore();
  const [globalFilterText, setGlobalFilterText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortField, setSortField] = useState<{
    field: keyof NewSecurity | "" | undefined;
    ascending: boolean;
  }>({ field: "name", ascending: false });

  const [dialog, setDialog] = useState<{
    open: boolean;
    data?: NewSecurity;
    mode?: "Edit" | "Delete";
  }>({
    open: false,
  });

  _newInstruments = useMemo(() => {
    try {
      if (!_newInstruments) return [];
      const filterResult = globalFilter(_newInstruments, globalFilterText);
      const sortResult = sort(
        filterResult,
        sortField?.field,
        sortField?.ascending
      );
      return sortResult;
    } catch (e) {
      return _newInstruments;
    }
  }, [_newInstruments, globalFilterText, sortField]);

  const currentTableData = useMemo(() => {
    _newInstruments = _newInstruments.filter(
      (instrument) => instrument.state === NewInstrumentState.Saved
    );
    const firstPageIndex = (currentPage - 1) * pageSize;
    const lastPageIndex = firstPageIndex + pageSize;
    return _newInstruments.slice(firstPageIndex, lastPageIndex);
  }, [_newInstruments, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [globalFilterText]);

  function handleAction(action: Action, data: NewSecurity) {
    if (action.name === "Edit" && !action.disabled) {
      setDialog({
        open: true,
        data,
        mode: "Edit",
      });
    } else if (action.name === "Delete" && !action.disabled) {
      setDialog({
        open: true,
        data,
        mode: "Delete",
      });
    }
  }

  const handleSubmit = async (id: string) => {
    const instrument =
      roundCreationState.roundCreation.tab5?.newInstruments || [];
    const instrumentList = instrument.map((element) => {
      if (element.id === id) {
        return {
          ...element,
          state: NewInstrumentState.ToBeDeleted,
        } as NewSecurity;
      } else {
        return element;
      }
    });
    const newList = {
      ...roundCreationState.roundCreation,
      tab5: {
        listOfInstruments:
          roundCreationState.roundCreation.tab5?.listOfInstruments ?? [],
        listOfShareHolders:
          roundCreationState.roundCreation.tab5?.listOfShareHolders ?? [],
        nonPricedInstruments:
          roundCreationState.roundCreation.tab5?.nonPricedInstruments,
        newInstruments: instrumentList,
        investorDetails:
          roundCreationState.roundCreation.tab5?.investorDetails || [],
      },
    };
    roundCreationState.sendMessage(newList);
    setDialog({ open: false });
  };

  return (
    <div className="bg-white rounded-lg">
      <Dialog open={dialog.open} maxWidth="lg">
        {dialog.mode === "Delete" && (
          <DeleteModal
            onPrimaryAction={() => {
              handleSubmit(dialog.data?.id || "");
            }}
            onSecondaryAction={() => setDialog({ open: false })}
            data={
              "Are you sure you want to delete the entry? Data will be permanently removed. This action cannot be undone."
            }
          />
        )}
        {dialog.mode === "Edit" && (
          <AddNewSecurity
            data={dialog.data}
            onBack={() => {
              setDialog({ open: false });
            }}
            actionMode="Edit"
          />
        )}
      </Dialog>
      <div className="flex flex-col">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full py-2 align-middle">
            <div className="md:rounded-lg">
              <table className="min-w-full bg-white">
                <thead>
                  <TableHeader>
                    <THead
                      fieldName="name"
                      fieldDisplayName="Name"
                      isFirstFeild={true}
                      selectedFieldName={sortField?.field || ""}
                      isAscending={sortField?.ascending || false}
                      isColumnFilter={false}
                      onChangeSort={() => {
                        setSortField({
                          field: "name",
                          ascending: !sortField?.ascending,
                        });
                      }}
                    />
                    <THead
                      fieldName="conversionRatio"
                      fieldDisplayName="Conversion Ratio"
                      showCurrency={false}
                      selectedFieldName={sortField?.field || ""}
                      isAscending={sortField?.ascending || false}
                      isColumnFilter={false}
                      onChangeSort={() => {
                        setSortField({
                          field: "conversionRatio",
                          ascending: !sortField?.ascending,
                        });
                      }}
                    />
                    <THead
                      fieldName="parValue"
                      fieldDisplayName="PAR"
                      showCurrency={true}
                      selectedFieldName={sortField?.field || ""}
                      isAscending={sortField?.ascending || false}
                      isColumnFilter={false}
                      onChangeSort={() => {
                        setSortField({
                          field: "parValue",
                          ascending: !sortField?.ascending,
                        });
                      }}
                    />
                    <THead
                      fieldName="type"
                      fieldDisplayName="Type"
                      selectedFieldName={sortField?.field || ""}
                      isAscending={sortField?.ascending || false}
                      isColumnFilter={false}
                      onChangeSort={() => {
                        setSortField({
                          field: "type",
                          ascending: !sortField?.ascending,
                        });
                      }}
                    />
                    <th className="hidden"></th>
                  </TableHeader>
                </thead>
                <tbody>
                  {currentTableData.map((captable: NewSecurity) => (
                    <tr
                      key={`${captable.id}`}
                      className="border-t whitespace-nowrap border-dashed hover:bg-slate-50 text-[#030303] text-xs2 font-medium "
                    >
                      <td className="py-4 pr-3 text-sm whitespace-nowrap ">
                        <VStack className="flex items-start">
                          {captable.name.length > 24 ? (
                            <Tooltip text={captable.name}>
                              <p className={tableFirstColStyle}>
                                {limitString(captable.name || "", 24)}
                              </p>
                            </Tooltip>
                          ) : (
                            <p className={tableFirstColStyle}>
                              {captable.name[0].toUpperCase() +
                                captable.name.slice(1)}
                            </p>
                          )}
                        </VStack>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <VStack className="items-center">
                          <p className={tableEntryStyle}>
                            {captable.conversionRatio}
                          </p>
                        </VStack>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <VStack className="items-center">
                          <p className={tableEntryStyle}>{captable.parValue}</p>
                        </VStack>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap">
                        <VStack className="items-center">
                          <p className={tableEntryStyle}>{captable.type}</p>
                        </VStack>
                      </td>

                      <td className="px-2 py-4 align-top">
                        <CTADropdown
                          dropdownClassName="bottom-auto"
                          actions={[
                            {
                              name: "Edit",
                              disabled: false,
                            },
                            {
                              name: "Delete",
                              disabled: false,
                            },
                          ]}
                          onAction={(action) => handleAction(action, captable)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {currentTableData.length === 0 && (
                <EmptyTable
                  header="No Security Available"
                  subHeader="start by adding new security"
                />
              )}
            </div>
          </div>
        </div>
        <Box className="flex justify-between my-4 ">
          {_newInstruments.length >= 5 && (
            <BasicMenu
              defaultValue={pageSize}
              options={[5, 10, 20, 50, 100]}
              onOptionChange={(value) => {
                setCurrentPage(1);
                setPageSize(value);
              }}
            />
          )}
          <Pagination
            currentPage={currentPage}
            totalCount={_newInstruments?.length}
            pageSize={pageSize}
            onPageChange={(page: number) => setCurrentPage(page)}
          />
        </Box>
      </div>
    </div>
  );
}
